import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type RowData,
} from "@tanstack/react-table";
import { useSyncExternalStore, type ReactNode } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

export interface DataTablePage {
  /** Zero-based. Lives in the URL upstream so a page is shareable. */
  pageIndex: number;
  pageSize: number;
  /** Total rows the SERVER holds — not the length of `rows`. */
  rowCount: number;
}

/** Which edge a column's content sits against. Numbers read down a right edge. */
export type DataTableAlign = "left" | "right";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /**
     * Alignment for BOTH the header cell and every body cell of this column, so
     * a right-aligned value column can never drift out of line with its header.
     */
    align?: DataTableAlign;
    /**
     * A share of the table's width ("30%"). When EVERY column names one, the
     * table lays out with those widths instead of sizing each column to its
     * content — so two tables with the same columns, stacked one under the
     * other with the second's header hidden, put every column in the same
     * place. Sized to content, each table chose its own widths and a hidden
     * header left the lower table's columns sitting under nothing.
     */
    width?: string;
    /** Marks a server-sortable column. DataTable renders the control; the caller owns the query. */
    sortable?: boolean;
    /** Token-only classes shared by this column's header and body cells. */
    className?: string;
  }
}

/**
 * A run of rows under one heading, rendered inside the SAME table as every
 * other group — one header row for the whole table, headings interleaved.
 *
 * A table per group repeats the entire column header under every heading, which
 * is what makes a grouped catalogue read as several unrelated tables.
 */
export interface DataTableGroup<TRow> {
  /** Stable across renders — React's key for the heading row. */
  id: string;
  title: string;
  rows: TRow[];
  /**
   * `primary` is the section's own subject (brand purple); `muted` is a
   * secondary grouping that must not compete with it; `inverted` is a full-width
   * dark bar for a group that must never be read as part of the rows above it
   * (retired pages under live ones).
   */
  tone?: "primary" | "muted" | "inverted";
}

interface DataTableBase<TRow> {
  columns: ColumnDef<TRow, unknown>[];
  /**
   * Required, not optional. Server pagination is the CONTRACT, not a feature a
   * module opts into: a module returning an unbounded collection fails its own
   * acceptance (dev spec §7.1 rule 3). Making this required means "I'll just
   * return everything" does not typecheck.
   */
  page: DataTablePage;
  onPageChange: (pageIndex: number) => void;
  pending?: boolean;
  /** Stated in the module's own words — "no rows" is a finding, not a blank. */
  emptyMessage?: string;
  caption?: string;
  /**
   * `compact` is the dense catalogue rhythm — a row you can scan a screenful of
   * at a time. `default` is the roomier reading rhythm.
   *
   * `catalogue` is `compact` PLUS the approved mock-up's chrome: grey hairline
   * rules and small bold grey column captions. It is a separate value rather
   * than a change to `compact` because `compact` is shared — pcogs runs, and
   * raw-materials materials/pool/log all ask for it, and none of those four has
   * ever been photographed against a mock-up. Restyling every rule and header
   * in them on the way past would be a rendered change to four surfaces this
   * PR's own screenshot gate cannot see, which is the exact failure the gate
   * exists to catch. They keep the rendering they had; PROTEX opts in.
   */
  density?: "default" | "compact" | "catalogue";
  /**
   * Per-row classes — the seam for a selected or de-emphasised row. A module
   * marks state here rather than re-styling a cell, so the whole row carries it.
   */
  rowClassName?: (row: TRow) => string | undefined;
  /** Optional drill-in action for a complete row. */
  onRowClick?: (row: TRow) => void;
  /**
   * Hide the header row VISUALLY, for a second table stacked under a first one
   * whose columns are identical — the reader has already read those labels and
   * a repeat is noise. Hidden, never absent: the `<th>` elements still exist,
   * so every cell keeps the column name a screen reader announces with it.
   * Never pass this for a table whose columns differ from the one above.
   */
  headerHidden?: boolean;
  /** Current server sort. Optional so existing tables remain unchanged. */
  sort?: { id: string; direction: "asc" | "desc" };
  /** Receives the column id; the module writes it to the URL and refetches. */
  onSort?: (id: string) => void;
  /**
   * Let header and body cells wrap. The Table primitive keeps every cell on one
   * line, which suits a short fact but paints a long label over its neighbour
   * once the columns carry fixed widths (bug ab37d90c: the Price Run at 1440).
   */
  wrap?: boolean;
  /**
   * Below 1280px (`xl`), render each row as a card: every column's header over
   * its value, stacked, so a wide grid never squeezes or scrolls sideways on a
   * phone or a narrow laptop (DEC-637, bug 4020402f). At 1280px and wider the
   * table is unchanged. Opt-in: a table that reads fine narrow keeps its rows.
   * Cards carry no sort control; a carded table sorts from its own toolbar.
   */
  cards?: boolean;
  /** Optional semantic table footer. Existing consumers omit it and render unchanged. */
  footer?: ReactNode;
}

type DataTableProps<TRow> = DataTableBase<TRow> &
  (
    | { rows: TRow[]; groups?: never }
    /** Grouped: the component flattens, so the caller never passes both. */
    | { groups: DataTableGroup<TRow>[]; rows?: never }
  );

/**
 * The shared table (C3, I-7). A TanStack Table wrapper over the shadcn Table
 * primitive — never a hand-rolled `<table>`, and never a second table idiom in
 * a module.
 *
 * `manualPagination` is hardcoded on: the portal is not a backend (I-4), so it
 * never holds a module's full collection in memory to slice client-side.
 */
export function DataTable<TRow>({
  columns,
  rows,
  groups,
  page,
  onPageChange,
  pending = false,
  emptyMessage = "No rows.",
  caption,
  density = "default",
  rowClassName,
  onRowClick,
  headerHidden = false,
  sort,
  onSort,
  wrap = false,
  cards = false,
  footer,
}: DataTableProps<TRow>) {
  const narrow = useNarrowViewport();
  const flatRows = groups ? groups.flatMap((group) => group.rows) : (rows ?? []);
  const pageCount = Math.max(1, Math.ceil(page.rowCount / Math.max(1, page.pageSize)));

  const table = useReactTable({
    data: flatRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount,
    state: { pagination: { pageIndex: page.pageIndex, pageSize: page.pageSize } },
  });

  // Where each group STARTS in the flattened row model. The heading is emitted
  // before the row at that index, so a group with no rows still gets its
  // heading — an empty domain is a fact about the catalogue, not a gap.
  const headingAt = new Map<number, DataTableGroup<TRow>>();
  if (groups) {
    let cursor = 0;
    for (const group of groups) {
      if (!headingAt.has(cursor)) headingAt.set(cursor, group);
      cursor += group.rows.length;
    }
  }

  // The RHYTHM — cell padding — is shared by both dense densities.
  const compact = density === "compact" || density === "catalogue";
  // The CHROME — rules and column captions — is the mock-up's, and only the
  // surface that was paired against it asks for it.
  const catalogue = density === "catalogue";
  /* `px-2`, not `px-3`. The brand body size is 20px (`--fs-body`), so every rem
     in this portal is 20px wide, and `px-3` spent 15px a side on five columns —
     30px more than the facts list has room for at 1440. The table then sat
     784px wide inside a 755px column and its own `overflow-x-auto` scrolled the
     LAST CHECKED header out of sight, which nobody discovers. At 10px a side
     the table fits with room to spare and the horizontal scroll goes back to
     being what it is for: a genuinely narrow viewport. */
  const cellClass = cn(compact ? "px-2 py-2.5 align-middle" : undefined, wrap && "whitespace-normal");
  /* Captions in the mock-up's voice: small, bold, letter-spaced, GREY — a label
     over the column, not a line of ink competing with the values under it.

     And every rule in `ks-grey-4`. This portal's base layer sets no default
     border colour, and Tailwind 4's default is `currentColor`, so shadcn's bare
     `border-b` on a row drew each divider in full ink — a black line under
     every fact where the mock-up draws a hairline. Scoped to `catalogue`, the
     one density whose screens have been paired against a mock-up; the missing
     base-layer default is a shell-wide question, not this table's to settle,
     and the four `compact` tables that were never photographed are not this
     PR's to restyle unseen. */
  const headClass = catalogue
    ? "h-auto px-2 pb-2 text-xs font-bold tracking-wider text-ks-grey-1 uppercase"
    : compact
      ? "h-auto px-2 pb-2 text-xs tracking-wide uppercase"
      : "h-auto px-2 pb-2 text-sm font-bold text-ks-purple";
  const ruleClass = catalogue ? "border-ks-grey-4" : undefined;

  const alignOf = (align: DataTableAlign | undefined) => (align === "right" ? "text-right" : undefined);

  const bodyRows = table.getRowModel().rows;
  // `<col>` widths, not widths on the header cells: a hidden header is out of
  // the table's layout, and a fixed layout would then size itself from the
  // first body row — a full-width group heading — and split every column evenly.
  const widths = table.getAllLeafColumns().map((column) => column.columnDef.meta?.width);
  const fixedWidths = widths.length > 0 && widths.every((width) => width !== undefined);

  // The card body. Each header is rendered once and reused on every card, so a
  // card names its fields exactly as the table's column captions do. A badge
  // may wrap inside a card: the primitive keeps one line, and a long missing-
  // figure badge then ran 500px wide and scrolled the page sideways at 390.
  const headerOf = new Map(
    table.getLeafHeaders().map((header) => [
      header.column.id,
      header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext()),
    ]),
  );
  const cardBody = (
    <ul aria-label={caption} className="flex flex-col gap-3" data-cards>
      {pending ? (
        Array.from({ length: Math.min(page.pageSize, 4) }, (_, i) => (
          <li key={`skeleton-${i}`} className="flex flex-col gap-2 rounded-md border border-ks-grey-4 p-3">
            {columns.map((_col, c) => (
              <Skeleton key={c} className="h-4 w-full" />
            ))}
          </li>
        ))
      ) : flatRows.length === 0 ? (
        <li className="rounded-md border border-ks-grey-4 p-3 text-sm text-ks-ink">{emptyMessage}</li>
      ) : (
        bodyRows.flatMap((row, index) => {
          const out: ReactNode[] = [];
          const heading = headingAt.get(index);
          if (heading) out.push(<GroupHeadingCard key={`h-${heading.id}`} group={heading} />);
          out.push(
            <li
              key={row.id}
              className={cn(
                "min-w-0 rounded-md border border-ks-grey-4 p-3 text-sm",
                rowClassName?.(row.original),
                onRowClick && "cursor-pointer",
              )}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              onKeyDown={
                onRowClick
                  ? (event) => {
                      if (event.target !== event.currentTarget) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onRowClick(row.original);
                      }
                    }
                  : undefined
              }
            >
              <dl className="grid min-w-0 gap-3 sm:grid-cols-2">
                {row.getVisibleCells().map((cell) => (
                  <div key={cell.id} className={cn("min-w-0 break-words", cell.column.columnDef.meta?.className)}>
                    <dt className="text-xs font-bold tracking-wide text-ks-grey-1 uppercase">{headerOf.get(cell.column.id)}</dt>
                    <dd className="mt-1 [&_[data-slot=badge]]:h-auto [&_[data-slot=badge]]:max-w-full [&_[data-slot=badge]]:whitespace-normal">{flexRender(cell.column.columnDef.cell, cell.getContext())}</dd>
                  </div>
                ))}
              </dl>
            </li>,
          );
          return out;
        })
      )}
    </ul>
  );

  return (
    <div>
      {cards && narrow ? cardBody : (
      <Table className={fixedWidths ? "table-fixed" : undefined}>
        {caption && <caption className="sr-only">{caption}</caption>}
        {fixedWidths && (
          <colgroup>
            {widths.map((width, i) => (
              <col key={i} style={{ width }} />
            ))}
          </colgroup>
        )}
        <TableHeader className={headerHidden ? "sr-only" : undefined}>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id} className={ruleClass}>
              {group.headers.map((header) => {
                const sortable = header.column.columnDef.meta?.sortable === true && !!onSort;
                const active = sort?.id === header.column.id;
                const ariaSort = sortable ? (active ? (sort.direction === "asc" ? "ascending" : "descending") : "none") : undefined;
                const SortIcon = active ? (sort.direction === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
                return (
                  <TableHead
                    key={header.id}
                    aria-sort={ariaSort}
                    className={cn(headClass, wrap && "whitespace-normal", alignOf(header.column.columnDef.meta?.align), header.column.columnDef.meta?.className)}
                  >
                    {header.isPlaceholder
                      ? null
                      : sortable
                        ? <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            data-nonmutating
                            className="h-auto gap-1 px-0 font-inherit text-inherit hover:bg-transparent"
                            onClick={() => onSort(header.column.id)}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            <SortIcon aria-hidden="true" className="size-3.5 text-ks-purple" />
                          </Button>
                        : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {pending ? (
            // Skeleton ROWS, not a spinner: the table keeps its shape, so
            // nothing below it moves when the data lands (UX S8 loading).
            //
            // No rule between them in `catalogue`. A skeleton row carries no fact,
            // so a divider there is a line under nothing — and it inherited the
            // bare `border-b` that draws in `currentColor`, which put a black
            // rule under every placeholder (outcome-audit cycle 3, M4). The
            // mock-up's loading table rules its header and nothing else.
            Array.from({ length: Math.min(page.pageSize, 8) }, (_, i) => (
              <TableRow
                key={`skeleton-${i}`}
                className={catalogue ? "border-b-0 hover:bg-transparent" : undefined}
              >
                {columns.map((_col, c) => (
                  <TableCell key={c} className={cellClass}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : flatRows.length === 0 ? (
            <TableRow className={ruleClass}>
              <TableCell colSpan={columns.length} className={cn(cellClass, "text-ks-ink")}>
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            bodyRows.flatMap((row, index) => {
              const out: ReactNode[] = [];
              const heading = headingAt.get(index);
              if (heading)
                out.push(<GroupHeadingRow key={`h-${heading.id}`} group={heading} span={columns.length} />);
              out.push(
                <TableRow
                  key={row.id}
                  className={cn(ruleClass, rowClassName?.(row.original), onRowClick && "cursor-pointer")}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onRowClick(row.original);
                          }
                        }
                      : undefined
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(cellClass, alignOf(cell.column.columnDef.meta?.align), cell.column.columnDef.meta?.className)}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>,
              );
              return out;
            })
          )}
        </TableBody>
        {footer && <TableFooter>{footer}</TableFooter>}
      </Table>
      )}

      {pageCount > 1 && (
        <nav aria-label="Pages" className="flex items-center gap-3 py-3 text-sm text-ks-ink">
          <Button
            variant="outline"
            size="sm"
            disabled={page.pageIndex <= 0}
            onClick={() => onPageChange(page.pageIndex - 1)}
          >
            Previous
          </Button>
          <span>
            Page {page.pageIndex + 1} of {pageCount} · {page.rowCount} rows
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page.pageIndex >= pageCount - 1}
            onClick={() => onPageChange(page.pageIndex + 1)}
          >
            Next
          </Button>
        </nav>
      )}
    </div>
  );
}

/**
 * A group heading, as a real `th scope="colgroup"` inside the body — so a
 * screen-reader user hears "NUTRITION" as the heading over the rows that follow
 * it, exactly as a sighted reader sees it, without a second table.
 */
function GroupHeadingRow<TRow>({ group, span }: { group: DataTableGroup<TRow>; span: number }) {
  return (
    <TableRow className={cn("border-b-0", group.tone === "inverted" ? "hover:bg-ks-ink" : "hover:bg-transparent")}>
      <TableHead
        scope="colgroup"
        colSpan={span}
        className={cn(
          "h-auto px-3 text-xs font-bold tracking-wider uppercase",
          group.tone === "inverted"
            ? "bg-ks-ink py-2 text-ks-surface"
            : cn("pt-4 pb-1.5", group.tone === "muted" ? "text-ks-grey-1" : "text-ks-purple"),
        )}
      >
        {group.title}
      </TableHead>
    </TableRow>
  );
}

/** A group heading in card mode: the same three tones as the table's heading row. */
function GroupHeadingCard<TRow>({ group }: { group: DataTableGroup<TRow> }) {
  return (
    <li
      className={cn(
        "px-3 text-xs font-bold tracking-wider uppercase",
        group.tone === "inverted"
          ? "rounded-md bg-ks-ink py-2 text-ks-surface"
          : cn("pt-2", group.tone === "muted" ? "text-ks-grey-1" : "text-ks-purple"),
      )}
    >
      {group.title}
    </li>
  );
}

/** Tailwind's `xl`: card mode applies below it. */
const WIDE_QUERY = "(min-width: 1280px)";

function subscribeWide(onChange: () => void) {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
  const query = window.matchMedia(WIDE_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

/** No matchMedia reads as wide, so the table renders exactly as before. */
function narrowSnapshot() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return !window.matchMedia(WIDE_QUERY).matches;
}

/** A server render is always wide: hydration then matches the table, and a narrow client switches to cards after. */
const wideOnServer = () => false;

function useNarrowViewport() {
  return useSyncExternalStore(subscribeWide, narrowSnapshot, wideOnServer);
}
